/**
 * **할인 쿠폰의 발급 한도·게시 기간·콜팝 랜덤팝업 한 곳** — 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지).
 *
 * 서버(appserver R-B)가 판정과 누계를 소유한다: 상태(`offerStatus`)는 저장하지 않고 파생하며, 발급 수·사용 누계도
 * 쿠폰 표에서 계산해 목록 응답에 싣는다. 이 파일은 그 어휘를 **문구로 옮기고**, 폼과 저장 본문 사이의 모양을 맞춘다.
 * 화면은 그리기만 한다.
 *
 * 계약 정본: `ordern-workspace/docs/kt-callpop/briefs/R-B-할인한도-appserver-report.md` §4 「admin-web 계약」.
 */
import { formatterNum } from './formatterNum.js'
import { toNumberOrNull } from './numberValue.js'

export const DISCOUNT_OFFER_TEXT = Object.freeze({
  issueLimitLabel: '건수 한도',
  budgetLimitLabel: '총액 한도(원)',
  offerPeriodLabel: '게시 기간',
  offerStartPlaceholder: '시작 제한 없음',
  offerEndPlaceholder: '끝 제한 없음',
  popupLabel: '콜팝 랜덤팝업 신청',
  unlimited: '무제한',
  /** 브리프 §3 문구 그대로 — 총액은 새 발급만 막으므로 사용 누계가 한도를 넘을 수 있다(D2) */
  limitHelp:
    '한도에 닿거나 기간이 끝나면 새 쿠폰 발급이 멈춥니다. 이미 받은 쿠폰은 유효기간까지 쓸 수 있어 사용 금액이 총액 한도를 조금 넘을 수 있습니다',
  /** 브리프 §3 문구 그대로 — 노출 페이지·빈도·순서는 콜팝 앱이 정한다(D9) */
  popupHelp: '랜덤팝업: 콜팝 앱이 팝업으로 알립니다. 노출 위치·빈도는 콜팝이 정합니다',
  statusColumn: '쿠폰 상태',
  issuedColumn: '발급 / 한도',
  usedColumn: '사용 / 한도',
})

/**
 * 목록 배지 — 서버 `offerStatus` 어휘 → 문구·색.
 *
 * `INACTIVE`(꺼졌거나 쿠폰 전용이 아님)는 배지 대상이 아니다. 모르는 값(구 서버·새 어휘)도 배지를 그리지 않는다.
 */
export const OFFER_STATUS_BADGE = Object.freeze({
  OPEN: Object.freeze({ label: '진행 중', type: 'success' }),
  NOT_STARTED: Object.freeze({ label: '예정', type: '' }),
  ENDED: Object.freeze({ label: '기간 종료', type: 'info' }),
  EXHAUSTED_COUNT: Object.freeze({ label: '건수 소진', type: 'warning' }),
  EXHAUSTED_BUDGET: Object.freeze({ label: '총액 소진', type: 'warning' }),
})

/**
 * @param {?string} offerStatus 서버 목록 응답의 `offerStatus`
 * @returns {?{label: string, type: string}} 배지가 없으면 `null`
 */
export function offerStatusBadge(offerStatus) {
  return Object.prototype.hasOwnProperty.call(OFFER_STATUS_BADGE, offerStatus) ? OFFER_STATUS_BADGE[offerStatus] : null
}

/**
 * 한도·게시 기간을 둘 수 있는 할인인가 — **쿠폰 사용 가능 + 쿠폰 전용**(D4). 그래야 모든 사용이 쿠폰을 거쳐 누계가 맞는다.
 *
 * 쿠폰 전용 체크박스는 쿠폰 사용 가능이 `N` 이면 숨지만 값(`couponOnlyYn`)은 폼에 남는다 — 그래서 두 칸을 함께 본다
 * (서버 `DiscountOfferSettings` 와 같은 식).
 *
 * KT R-T 결정 ①(2026-10-12)부터 **쿠폰을 낼 수 있는 할인**도 이 판정 하나다(서버 `DiscountCouponOnly.of`) — 쿠폰 발송 창의
 * 고를 할인(`couponSend.js`)과 아래 목록의 발급·사용 누계 칸이 함께 쓴다. 같은 식을 다른 곳에 다시 적지 않는다.
 */
export function isCouponOnlyDiscount(discount) {
  return !!discount && discount.couponApplicableYn === 'Y' && discount.couponOnlyYn === 'Y'
}

/** 폼의 빈 숫자 칸 — `null` 이 아니라 `undefined` 다(아래 [discountOfferForm]) */
const emptyToUndefined = (value) => {
  const number = toNumberOrNull(value)

  return number === null ? undefined : number
}

/**
 * **폼 표현** — 할인 응답(또는 없음)에서 새 칸 다섯을 폼에 담을 모양으로.
 *
 * ★ 빈 숫자 칸은 **`undefined`** 다. Element UI 2.15 `el-input-number` 는 `value` 가 `null` 이면
 * `Number(null) === 0` 을 `min`(1)으로 올려 **즉시 `input` 을 emit** 한다 — 서버가 「무제한」으로 준 `null` 을
 * 폼에 그대로 넣으면 칸이 그려지는 순간 한도 1 이 되고, 저장하면 그 1 이 서버에 들어간다. `undefined` 만 빈 칸으로 남는다.
 *
 * @param {?object} source 할인 상세 응답(없으면 등록 폼의 기본값)
 */
export function discountOfferForm(source) {
  const origin = source || {}

  return {
    issueLimitCnt: emptyToUndefined(origin.issueLimitCnt),
    budgetLimitAmt: emptyToUndefined(origin.budgetLimitAmt),
    offerStartDt: origin.offerStartDt || null,
    offerEndDt: origin.offerEndDt || null,
    popupRequestedYn: origin.popupRequestedYn === 'Y' ? 'Y' : 'N',
  }
}

/**
 * **저장 본문** — 폼(원본은 바뀌지 않는다)의 얕은 사본에 새 칸 다섯을 서버 모양으로 싣는다.
 *
 * 서버 할인 저장은 **전체 덮어쓰기**다: 본문에 없는 칸은 한도·기간 = `null`(무제한), 팝업 = `N` 으로 저장된다.
 * 그래서 다섯 칸은 **언제나** 명시적으로 싣는다(`undefined` 는 JSON 에서 빠지므로 `null` 로 바꾼다).
 *
 * - 쿠폰 전용이 아니면 한도·기간 네 칸을 비운다 — 값이 있으면 서버가 400 이다(D4). 칸이 숨었으니 사람도 못 본다.
 * - 랜덤팝업도 이 화면에서는 쿠폰 전용일 때만 보이므로(브리프 §3) 그 밖에는 `N` 이다 — 숨은 `Y` 가 파트너 목록에
 *   남지 않게 한다.
 * - 신청 시각(`popupRequestedDt`)은 서버가 정한다(요청 값 무시) — 손대지 않는다.
 *
 * @param {object} form 할인 입력 폼
 * @returns {object} 저장 요청 본문
 */
export function discountSavePayload(form) {
  const origin = form || {}
  const couponOnly = isCouponOnlyDiscount(origin)

  return Object.assign({}, origin, {
    issueLimitCnt: couponOnly ? toNumberOrNull(origin.issueLimitCnt) : null,
    budgetLimitAmt: couponOnly ? toNumberOrNull(origin.budgetLimitAmt) : null,
    offerStartDt: couponOnly ? origin.offerStartDt || null : null,
    offerEndDt: couponOnly ? origin.offerEndDt || null : null,
    popupRequestedYn: couponOnly && origin.popupRequestedYn === 'Y' ? 'Y' : 'N',
  })
}

const count = (value) => String(formatterNum(null, null, value))
const won = (value) => '₩' + count(value)

/**
 * 쿠폰을 발급할 수 없는 할인(또는 누계가 없는 구 서버 응답)은 칸을 비운다 — 발급할 수 있는 할인은 쿠폰 전용 할인뿐이다
 * ([isCouponOnlyDiscount], KT R-T 결정 ①). 상시할인(쿠폰 사용 가능 · 전용 아님)은 서버 상태도 `INACTIVE` 라 배지가 없다
 */
const hasCouponUsage = (row, key) => isCouponOnlyDiscount(row) && row[key] != null

/**
 * 목록 「발급 / 한도」 — `40 / 100`, 한도가 없으면 `40 / 무제한`. 발급 수는 회수·만료돼도 줄지 않는다(D1).
 */
export function issuedSummary(row) {
  if (!hasCouponUsage(row, 'issuedCnt')) return '-'

  const limit = row.issueLimitCnt == null ? DISCOUNT_OFFER_TEXT.unlimited : count(row.issueLimitCnt)

  return `${count(row.issuedCnt)} / ${limit}`
}

/**
 * 목록 「사용 / 한도」 — `₩120,000 / ₩300,000`, 한도가 없으면 `₩120,000 / 무제한`.
 * 사용 누계는 한도를 넘을 수 있다(이미 받은 쿠폰은 유효기간까지 쓰인다 — D2) — 그대로 보인다.
 */
export function usedSummary(row) {
  if (!hasCouponUsage(row, 'usedDiscountAmt')) return '-'

  const limit = row.budgetLimitAmt == null ? DISCOUNT_OFFER_TEXT.unlimited : won(row.budgetLimitAmt)

  return `${won(row.usedDiscountAmt)} / ${limit}`
}
